import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, ArrowRight } from 'lucide-react';

const FAQ = () => {
  // Only showing first 5 FAQs
  const faqs = [
    {
      question: "How does Cittasya® contribute to wellness?",
      answer: "Wellness is a lifestyle that promotes holistic health and vitality. As multidimensional beings, true well-being requires nurturing our physical, energetic, mental, emotional, and spiritual aspects. Yoga Nidra supports this by rejuvenating and harmonizing all layers of the self, fostering optimal health and helping us truly thrive."
    },
    {
      question: "What is Yoga Nidra?",
      answer: "Yoga Nidra is a powerful technique where one learns to relax consciously and counteract stress. It is known as psychic sleep where the awareness is held at the threshold of wakefulness and sleep. The practice provides a blissful and refreshing experience."
    },
    {
      question: "How long does each practice session take?",
      answer: "Initially, sessions begin at 15-20 minutes and may gradually extend up to 35 minutes as one progresses through the stages."
    },
    {
      question: "Why should one practise Yoga Nidra?",
      answer: "Yoga Nidra teaches the art of proper relaxation. In the hypnagogic state of Yoga Nidra, the greatest amount of physical, emotional and mental tensions are released. In this state, as the consciousness is kept away from external sensory awareness and sleep, it becomes very powerful and can be used for deep relaxation, therapeutic applications, increased focus and memory, and reformed behaviour & personality."
    },
    {
      question: "How should one practise Yoga Nidra?",
      answer: "One should lie down in savasana on a yoga mat, undisturbed and still for the duration of the practice, while remaining awake and aware. The room should be sufficiently lit and ventilated. Avoid lying down directly under the running fan. If there are mobility issues in getting down on the floor, one may practise in a comfortable sitting position supported by props. Avoid practising on the bed where you sleep."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Geometric Pattern Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 100% 100%, rgba(176, 224, 233, 0.12) 0, transparent 40%),
            radial-gradient(circle at 0% 0%, rgba(192, 235, 192, 0.12) 0, transparent 40%),
            linear-gradient(45deg, transparent 48%, rgba(176, 224, 233, 0.09) 49%, rgba(176, 224, 233, 0.09) 51%, transparent 52%),
            linear-gradient(-45deg, transparent 48%, rgba(192, 235, 192, 0.09) 49%, rgba(192, 235, 192, 0.09) 51%, transparent 52%)
          `,
          backgroundSize: '100% 100%, 100% 100%, 30px 30px, 30px 30px'
        }}></div>
        
        {/* Subtle dot pattern overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(rgba(176, 224, 233, 0.2) 1px, transparent 1px)`,
          backgroundSize: '20px 20px'
        }}></div>
        
        {/* Organic shapes */}
        <div className="absolute top-0 right-0 w-96 h-96" style={{
          background: 'radial-gradient(circle at center, rgba(192, 235, 192, 0.12) 0%, transparent 70%)'
        }}></div>
        <div className="absolute bottom-0 left-0 w-96 h-96" style={{
          background: 'radial-gradient(circle at center, rgba(176, 224, 233, 0.12) 0%, transparent 70%)'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="max-w-6xl mx-auto"
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-soft-saffron/10 border border-soft-saffron/20 mb-6">
              <HelpCircle className="w-4 h-4 text-soft-saffron" />
              <span className="text-sm font-medium text-forest-green">Frequently Asked Questions</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest-green mb-6">
              Common Questions About <span className="text-soft-saffron">Yoga Nidra</span>
            </h2>
            <p className="text-lg text-copper-brown/80 max-w-2xl mx-auto">
              Find answers to commonly asked questions about Yoga Nidra practice and its benefits.
            </p>
          </motion.div>

          {/* FAQ Accordion */}
          <motion.div variants={itemVariants} className="space-y-6">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white/50 backdrop-blur-sm rounded-lg border border-misty-sage/20 shadow-sm hover:border-soft-saffron/40 transition-all duration-300 p-1"
                >
                  <AccordionTrigger className="px-6 py-5 text-left font-medium text-forest-green hover:text-soft-saffron transition-colors duration-300">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5 text-copper-brown">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* View All FAQs Link */}
            <div className="text-center mt-12">
              <Link 
                to="/faq"
                onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }}
                className="inline-flex  border border-soft-saffron rounded-full px-4 py-2  mt-2 items-center gap-2 text-soft-saffron hover:text-forest-green transition-colors duration-300 font-medium"
              >
                View All FAQs
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ; 